import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { WarningCircle, Check } from 'iconoir-react';

interface AdminAlert {
  id: number;
  user: { id: number; name: string; email: string } | null;
  type: string;
  severity: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const AdminAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const response = await api.get('/admin/alerts');
      setAlerts(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des alertes:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId: number) => {
    try {
      await api.post(`/admin/alerts/${alertId}/mark-read`);
      await loadAlerts();
    } catch (error) {
      console.error('Erreur lors du marquage:', error);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      quota_exceeded: '📊 Quota dépassé',
      payment_failed: '💳 Paiement échoué',
      gdpr_request: '🔒 Demande RGPD',
      support_ticket: '💬 Ticket support',
      suspicious_activity: '⚠️ Activité suspecte',
    };
    return labels[type] || type;
  };

  const getSeverityBadge = (severity: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      info: { label: 'Info', color: '#3498db' },
      warning: { label: 'Attention', color: '#f39c12' },
      critical: { label: 'Critique', color: '#e74c3c' },
    };
    return badges[severity] || badges.info;
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="admin-alerts">
      <div className="section-header">
        <h2>Alertes administrateur</h2>
        <p>{alerts.filter(a => !a.is_read).length} non lue(s)</p>
      </div>

      <div className="alerts-list">
        {alerts.length === 0 ? (
          <div className="empty-state">Aucune alerte</div>
        ) : (
          alerts.map((alert) => {
            const severityBadge = getSeverityBadge(alert.severity);
            return (
              <div
                key={alert.id}
                className={`alert-card ${alert.is_read ? 'read' : 'unread'}`}
              >
                <div className="alert-header">
                  <div className="alert-type">
                    {getTypeLabel(alert.type)}
                  </div>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: severityBadge.color }}
                  >
                    {severityBadge.label}
                  </span>
                </div>
                <div className="alert-content">
                  <h3>{alert.title}</h3>
                  <p>{alert.message}</p>
                  {alert.user && (
                    <div className="alert-user">
                      <small>
                        Utilisateur : <strong>{alert.user.name}</strong> ({alert.user.email})
                      </small>
                    </div>
                  )}
                </div>
                <div className="alert-footer">
                  <small>{new Date(alert.created_at).toLocaleString('fr-FR')}</small>
                  {!alert.is_read && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => markAsRead(alert.id)}
                    >
                      <Check width={16} height={16} strokeWidth={2} />
                      Marquer comme lu
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminAlerts;
